import React from 'react';
import { DesignSystem } from '../styles/designSystem';
import { RISK_LEVELS, normalizeRiskLevel, type RiskLevel } from '../constants/project';

interface TaskRisksWidgetProps {
  projects: any[];
  visibleProjectIds?: string[]; // If provided, only count tasks from these projects
  riskFilter?: string; // 'all', 'high', 'medium', 'low', 'critical', 'none'
  onRiskFilter?: (riskLevel: string) => void;
}

interface RiskStats {
  [RISK_LEVELS.NONE]: number;
  [RISK_LEVELS.LOW]: number;
  [RISK_LEVELS.MEDIUM]: number;
  [RISK_LEVELS.HIGH]: number;
  [RISK_LEVELS.CRITICAL]: number;
}

const RiskColors = {
  [RISK_LEVELS.NONE]: DesignSystem.colors.semantic.risk.none,
  [RISK_LEVELS.LOW]: DesignSystem.colors.semantic.risk.low,
  [RISK_LEVELS.MEDIUM]: DesignSystem.colors.semantic.risk.medium,
  [RISK_LEVELS.HIGH]: DesignSystem.colors.semantic.risk.high,
  [RISK_LEVELS.CRITICAL]: DesignSystem.colors.semantic.risk.critical,
};

const RiskLabels = {
  [RISK_LEVELS.NONE]: 'No Risk',
  [RISK_LEVELS.LOW]: 'Low',
  [RISK_LEVELS.MEDIUM]: 'Medium',
  [RISK_LEVELS.HIGH]: 'High',
  [RISK_LEVELS.CRITICAL]: 'Critical',
};

const widgetFont = { fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' };

export const TaskRisksWidget: React.FC<TaskRisksWidgetProps> = ({ projects, visibleProjectIds, riskFilter = 'all', onRiskFilter }) => {
  // Normalize risk values using centralized utility
  const normalizeRisk = (risk: string | null | undefined): RiskLevel => {
    return normalizeRiskLevel(risk);
  };

  // Calculate task risk statistics from active projects
  // If visibleProjectIds provided, only count those projects; otherwise filter by activity heuristics
  const validProjects = projects
    .filter(project => {
      // If visibleProjectIds provided, only include those
      if (visibleProjectIds && visibleProjectIds.length > 0) {
        return visibleProjectIds.includes(project.id);
      }

      // Fallback: filter by activity heuristics
      const hasValidLocation = project.sites?.some((site: any) =>
        site?.geo_location && Array.isArray(site.geo_location) && site.geo_location.length === 2
      );
      const isNotCompleted = project.status !== 'COMPLETED';
      const hasItems = (project.items?.length || 0) > 0;
      const hasActionItems = (project.actionItems?.length || 0) > 0;
      const isNotTest = !/\b(test|copy|demo|tester|sample)\b/i.test(project.name || '');
      const totalTasks = (project.items?.length || 0) + (project.actionItems?.length || 0);
      const hasMeaningfulLoad = totalTasks >= 10;
      return hasValidLocation && isNotCompleted && hasItems && hasActionItems && isNotTest && hasMeaningfulLoad;
    });


  // Only count incomplete tasks (not COMPLETED status)
  const allTasks = validProjects.flatMap(project => [
    ...(project.items || []).filter((item: any) => item.status !== 'COMPLETED'),
    ...(project.actionItems || []).filter((item: any) => item.status !== 'COMPLETED')
  ]);

  // Always compute stats from ALL tasks (unfiltered) so the widget is always interactive
  const allStats: RiskStats = allTasks.reduce((acc, task) => {
    const risk = normalizeRisk(task.risks);
    acc[risk] = acc[risk] + 1;
    return acc;
  }, {
    [RISK_LEVELS.NONE]: 0,
    [RISK_LEVELS.LOW]: 0,
    [RISK_LEVELS.MEDIUM]: 0,
    [RISK_LEVELS.HIGH]: 0,
    [RISK_LEVELS.CRITICAL]: 0
  });

  if (visibleProjectIds && riskFilter === 'low') {
    console.log('Low risk tasks from visible projects:', allTasks.filter((t: any) => normalizeRisk(t.risks) === RISK_LEVELS.LOW).map((t: any) => ({ name: t.name, risk: t.risks })));
  }

  const totalAll = allTasks.length;

  if (totalAll === 0) {
    return (
      <div className="bg-white rounded-xl border border-[#eaeaea] shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-5 w-60" style={widgetFont}>
        <h3 className="text-[13px] font-semibold text-[#171717] mb-4 tracking-[-0.01em]">Task Risks</h3>
        <div className="flex items-center justify-center h-20 text-[#888]">
          <span className="text-[13px]">No tasks</span>
        </div>
      </div>
    );
  }

  // For display, use allStats always so all risk levels remain clickable
  const stats = allStats;
  const total = totalAll;

  // Calculate percentages and prepare data for display
  const riskData = Object.entries(stats).map(([risk, count]) => ({
    risk: risk as keyof RiskStats,
    count,
    percentage: total > 0 ? (count / total * 100) : 0,
    color: RiskColors[risk as keyof RiskStats],
    label: RiskLabels[risk as keyof RiskStats]
  }));

  // When filter is applied, show only that risk level
  // When no filter, show all risk levels (excluding NONE unless it has tasks)
  const riskLevelsToShow = riskFilter === 'all'
    ? Object.values(RISK_LEVELS).filter(level => level !== RISK_LEVELS.NONE || stats[RISK_LEVELS.NONE] > 0)
    : [normalizeRiskLevel(riskFilter)];

  const orderedRiskData = riskLevelsToShow.map(riskLevel =>
    riskData.find(item => item.risk === riskLevel) || {
      risk: riskLevel,
      count: 0,
      percentage: 0,
      color: RiskColors[riskLevel],
      label: RiskLabels[riskLevel]
    }
  );

  // Find max count for scaling bars (avoid division by zero)
  const maxCount = Math.max(...riskData.map(item => item.count), 1);

  // Get title based on filter
  const getTitle = () => {
    if (riskFilter === 'all') return 'Task Risks';
    return `${riskFilter.charAt(0).toUpperCase() + riskFilter.slice(1)} Risk`;
  };

  return (
    <div className="bg-white rounded-xl border border-[#eaeaea] shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-5 w-60 relative" style={{ ...widgetFont, zIndex: 1 }}>
      <h3 className="text-[13px] font-semibold text-[#171717] mb-4 tracking-[-0.01em]">{getTitle()}</h3>

      <div className="space-y-2.5">
        {orderedRiskData.map((item) => {
          const isActive = riskFilter !== 'all' && item.risk.toLowerCase() === riskFilter.toLowerCase();
          const isClickable = !!onRiskFilter;

          return (
            <div
              key={item.risk}
              className={`space-y-1.5 rounded-md px-2 py-1.5 -mx-2 transition-all duration-150 ${
                isClickable ? 'cursor-pointer hover:bg-[#fafafa]' : ''
              } ${isActive ? 'bg-[#fafafa] border border-[#eaeaea]' : 'border border-transparent'}`}
              onClick={() => {
                if (!onRiskFilter) return;
                onRiskFilter(isActive ? 'all' : item.risk.toLowerCase());
              }}
            >
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <div
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-[12px] font-medium text-[#444]">
                    {item.label}
                  </span>
                </div>
                <div className="text-[11px] text-[#888] tabular-nums">
                  {item.count} {item.count > 0 && `(${Math.round(item.percentage)}%)`}
                </div>
              </div>

              {/* Bar */}
              <div className="w-full bg-[#eaeaea] rounded-full h-1.5">
                <div
                  className="h-1.5 rounded-full transition-all duration-500 ease-out"
                  style={{
                    backgroundColor: item.color,
                    width: item.count > 0 ? `${(item.count / maxCount) * 100}%` : '0%'
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary Stats */}
      <div className="mt-4 pt-3 border-t border-[#eaeaea]">
        <div className="grid grid-cols-2 gap-3 text-center">
          <div>
            <div className="text-[15px] font-semibold text-red-600 tabular-nums">
              {riskFilter !== 'all' ? stats[normalizeRiskLevel(riskFilter)] : stats.HIGH}
            </div>
            <div className="text-[11px] text-[#888]">{getTitle().replace('Task Risks', '').trim() || 'High Risk'}</div>
          </div>
          <div>
            <div className="text-[15px] font-semibold text-[#171717] tabular-nums">
              {total}
            </div>
            <div className="text-[11px] text-[#888]">Total</div>
          </div>
        </div>
      </div>
    </div>
  );
};
